<template>
  <div class="relative min-h-screen pb-24 bg-white overflow-x-hidden">
    <!-- Loader animasi di tengah layar -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-white z-[9999]"
    >
      <LottieLoader :src="lottieJson" />
    </div>

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      class="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] bg-red-500 text-white px-6 py-3 rounded-lg shadow-xl"
    >
      {{ errorMessage }}
    </div>

    <div
      class="relative h-60 bg-cover bg-center bg-no-repeat"
      :style="headerBackgroundStyle"
    >
      <div class="absolute inset-0 bg-black/50 z-10" />

      <div
        class="absolute left-1/2 z-20 text-center whitespace-nowrap px-4"
        style="top: 25%; transform: translate(-50%, -50%)"
      >
        <h1 class="text-2xl font-bold text-cream">
          Menu Pemesanan
        </h1>
        <p class="text-sm text-cream">
          Silakan pilih menu yang ingin dipesan
        </p>
      </div>

      <div class="absolute bottom-4 left-0 right-0 px-4 z-30">
        <div
          class="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center bg-white/70 backdrop-blur-lg p-3 rounded-lg shadow-lg border border-white/80"
        >
          <!-- Filter kategori - Responsif untuk Z Fold -->
          <div class="w-full sm:flex-1 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
            <div class="flex gap-2 min-w-max">
              <button
                :class="[
                  'px-4 py-2 rounded-2xl whitespace-nowrap border text-sm sm:text-base shadow transition flex-shrink-0',
                  selectedCategory === 'best'
                    ? 'bg-espresso text-cream border-espresso font-semibold'
                    : 'bg-white/70 border-gray-300 text-roast hover:bg-gray-100/80',
                ]"
                @click="selectCategory('best')"
              >
                Best Seller
              </button>
              <button
                v-for="category in allCategories"
                :key="category.id"
                :class="[
                  'px-4 py-2 rounded-2xl whitespace-nowrap border text-sm sm:text-base shadow transition flex-shrink-0',
                  selectedCategory === category.id
                    ? 'bg-espresso text-cream border-espresso font-semibold'
                    : 'bg-white/70 border-gray-300 text-roast hover:bg-gray-100/80',
                ]"
                @click="selectCategory(category.id)"
              >
                {{ category.name }}
              </button>
            </div>
          </div>

          <!-- Search Bar & Actions - Responsif untuk Z Fold -->
          <div class="flex items-center gap-2 w-full sm:w-auto sm:min-w-[280px]">
            <!-- Tombol Refresh -->
            <button
              :disabled="isRefreshing"
              :title="isRefreshing ? 'Memperbarui menu...' : 'Refresh menu'"
              class="flex-shrink-0 p-2.5 rounded-full bg-espresso hover:bg-mocha text-cream transition-all duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{ 'refresh-spin': isRefreshing }"
              @click="refreshMenu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5"
                :class="{ 'animate-spin': isRefreshing }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>

            <!-- Search Input -->
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Cari menu..."
              class="border-none text-espresso flex-1 min-w-0 px-3 py-2.5 rounded-full focus:outline-none focus:border-espresso bg-white/70 text-sm sm:text-base placeholder:text-gray-500"
              @input="onSearchInput"
            >
            
            <!-- Logo -->
            <img
              :src="config.LOGO_URL"
              alt="Logo"
              class="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 rounded-full object-contain shadow-md"
            >
          </div>
        </div>
      </div>
    </div>

    <div
      class="px-2 mt-4 relative overflow-hidden"
      style="min-height: 400px"
    >
      <!-- Skeleton Loading saat refresh atau transisi kategori -->
      <div
        v-if="isRefreshing || isTransitioning"
        class="relative"
      >
        <div v-if="tempSelectedCategory === 0 || selectedCategory === 0">
          <div
            v-for="category in categories.slice(0, 3)"
            :key="'skeleton-' + category.id"
            class="mb-6"
          >
            <div class="h-6 w-32 bg-latte/30 rounded mb-2 skeleton-shimmer" />
            <hr class="border-t border-gray-300 mb-4">
            <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div
                v-for="i in 6"
                :key="'skeleton-item-' + i"
                class="skeleton-card"
              >
                <div class="skeleton-image" />
                <div class="skeleton-content">
                  <div class="skeleton-title" />
                  <div class="skeleton-price" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="h-6 w-32 bg-latte/30 rounded mb-2 skeleton-shimmer" />
          <hr class="border-t border-gray-300 mb-4">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div
              v-for="i in 8"
              :key="'skeleton-item-' + i"
              class="skeleton-card"
            >
              <div class="skeleton-image" />
              <div class="skeleton-content">
                <div class="skeleton-title" />
                <div class="skeleton-price" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu Items - Render Semua Langsung -->
      <div
        v-show="!isRefreshing && !isTransitioning"
        class="relative"
      >
        <transition
          :name="transitionName"
          mode="out-in"
        >
          <div
            :key="selectedCategory"
            class="relative"
          >
            <div v-if="selectedCategory === 0">
              <div
                v-if="!anyResults"
                class="text-center text-mocha mt-12 py-8"
              >
                <p class="text-lg font-semibold mb-2">
                  Menu Tidak Tersedia
                </p>
                <p class="text-sm text-gray-600">
                  Maaf, saat ini belum ada menu yang tersedia atau stok telah habis
                </p>
              </div>
              <div v-else>
                <div
                  v-for="category in categoriesToShow"
                  :key="category.id"
                  class="mb-4"
                >
                  <h2 class="text-lg font-bold text-mocha mb-2 text-left">
                    {{ category.name }}
                  </h2>
                  <hr class="border-t border-gray-500 mb-4">
                  <div
                    class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4"
                  >
                    <MenuItem
                      v-for="item in getCachedFilteredMenus(category.id)"
                      :key="`${item.id}-${item.categoryId}`"
                      :item="item"
                      :active-variant-id="activeVariantId"
                      :set-active-variant="setActiveVariant"
                      :best-seller-ids="bestSellerIds"
                      :get-menu-variants="getMenuVariants"
                      @add-to-cart="handleAddToCart"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else>
              <h2 class="text-lg font-bold text-mocha mb-2 text-left">
                {{ currentCategoryName }}
              </h2>
              <hr class="border-t border-gray-500 mb-4">
              
              <!-- Tampilan khusus untuk Best Seller kosong -->
              <div
                v-if="selectedCategory === 'best' && filteredMenus.length === 0"
                class="text-center text-mocha mt-12 py-8"
              >
                <p class="text-lg font-semibold mb-2">
                  Belum Ada Data Best Seller
                </p>
                <p class="text-sm text-gray-600">
                  Data best seller akan muncul setelah ada pesanan yang selesai
                </p>
              </div>
              
              <div
                v-else-if="filteredMenus.length === 0"
                class="text-center text-mocha mt-12 py-8"
              >
                <p class="text-lg font-semibold mb-2">
                  Menu Tidak Tersedia
                </p>
                <p class="text-sm text-gray-600">
                  Maaf, saat ini belum ada menu yang tersedia atau stok telah habis
                </p>
              </div>
              <div
                v-else
                class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              >
                <MenuItem
                  v-for="item in filteredMenus"
                  :key="`${item.id}-${item.categoryId}`"
                  :item="item"
                  :active-variant-id="activeVariantId"
                  :set-active-variant="setActiveVariant"
                  :best-seller-ids="bestSellerIds"
                  :get-menu-variants="getMenuVariants"
                  @add-to-cart="handleAddToCart"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Cart Button dengan animasi smooth -->
    <button
      :title="
        cartCount +
          (cartCount > 1 ? ' items di keranjang' : ' item di keranjang')
      "
      class="fixed bottom-4 right-4 z-50 flex items-center overflow-hidden shadow-xl text-cream bg-[#805b2f] transition-all duration-300 ease-in-out flex-nowrap"
      :class="[
        cartCount > 0
          ? 'w-32 h-14 rounded-3xl px-4 gap-2'
          : 'w-14 h-14 rounded-full p-0 justify-center',
        { 'cart-animate': isCartAnimating },
      ]"
      @click="goToCart"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-6 h-6 flex-shrink-0 transition-transform duration-500 ease-in-out"
        :class="{ 'cart-rotate': isCartAnimating }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle
          cx="9"
          cy="21"
          r="1"
        />
        <circle
          cx="20"
          cy="21"
          r="1"
        />
        <path
          d="M1 1h4l2.68 13.39a1 1 0 001 .61h9.72a1 1 0 00.98-.79l1.38-7.38H6"
        />
      </svg>

      <span
        v-if="cartCount > 0"
        class="flex-grow text-left font-bold text-cream select-none transition-all duration-300 ease-in-out"
        :class="{ 'cart-label-bounce': isCartAnimating }"
        style="font-size: 0.95rem"
      >
        {{ cartCount }} {{ cartCount > 1 ? "items" : "item" }}
      </span>
    </button>

    <!-- Loader saat navigate ke CartPage -->
    <div
      v-if="isNavigatingToCart"
      class="fixed inset-0 flex items-center justify-center bg-white z-[9999]"
    >
      <LottieLoader :src="loaderJson" />
    </div>

    <!-- Variant Modal -->
    <VariantModal
      :is-open="isModalOpen"
      :product="selectedProduct"
      :variants="selectedProductVariants"
      :additionals="selectedProduct.additionals || []"
      @close="closeModal"
      @add-to-cart="handleAddFromModal"
    />
  </div>
</template>

<script>
import MenuItem from "@/components/MenuItem.vue";
import LottieLoader from "@/components/LottieLoader.vue";
import VariantModal from "@/components/VariantModal.vue";
import api from "@/api";
import lottieJson from "../assets/loader.json";
import loaderJson from "../assets/loader.json";

const CONFIG = {
  BEST_SELLER_LIMIT: 10,
  BEST_SELLER_DAYS: 90,
  ANIMATION: {
    SEARCH_DEBOUNCE: 300,
    CART_ANIMATION: 600,
    REFRESH_DELAY: 500,
    CATEGORY_TRANSITION_START: 150,
    CATEGORY_TRANSITION_END: 500,
  },
  ASSETS_BASE_URL: "https://sejadikopi-api-v2.sejadikopi.com/storage",
  SPECIAL_CATEGORIES: {
    DIMSUM: ["dimsum", "dim sum"],
  },
};

CONFIG.LOGO_URL = `${CONFIG.ASSETS_BASE_URL}/Logo/sejadikopi_circle.png`;
CONFIG.BANNER_URL = `${CONFIG.ASSETS_BASE_URL}/images/banner.jpg`;

export default {
  name: "MenuPage",
  components: {
    MenuItem,
    LottieLoader,
    VariantModal,
  },
  emits: ["add-to-cart"],

  data() {
    return {
      config: CONFIG,
      searchQuery: "",
      debouncedSearchQuery: "",
      searchDebounceTimer: null,
      categories: [],
      selectedCategory: "best",
      previousCategory: 0,
      slideDirection: "left",
      menus: [],
      bestSellerIds: [],
      specialCategories: {
        dimsum: [],
      },
      cart: [],
      cartCount: 0,
      activeVariantId: null,
      isCartAnimating: false,
      isLoading: true,
      isRefreshing: false,
      isTransitioning: false,
      tempSelectedCategory: null,
      errorMessage: null,
      menuCache: new Map(),
      lottieJson,
      loaderJson,
      // ✅ TAMBAHAN: Modal State
      isModalOpen: false,
      selectedProduct: {},
      selectedProductVariants: [],
      // ✅ TAMBAHAN: Navigation Loading State
      isNavigatingToCart: false,
    };
  },

  computed: {
    queryString() {
      return (this.debouncedSearchQuery || "").trim().toLowerCase();
    },

    allCategories() {
      return [{ id: 0, name: "Semua" }, ...this.categories];
    },

    currentCategoryName() {
      if (this.selectedCategory === "best") return "Best Seller";
      const category = this.categories.find(
        (c) => c.id === this.selectedCategory
      );
      return category ? category.name : "";
    },

    categoryOrder() {
      return ["best", 0, ...this.categories.map((c) => c.id)];
    },

    transitionName() {
      return this.slideDirection === "left" ? "slide-left" : "slide-right";
    },

    filteredMenus() {
      const q = this.queryString;
      if (this.selectedCategory === "best") {
        return this.menus.filter(
          (menu) =>
            this.bestSellerIds.includes(menu.id) &&
            (menu.name || "").toLowerCase().includes(q)
        );
      }
      return this.menus.filter(
        (menu) =>
          menu.categoryId === this.selectedCategory &&
          (menu.name || "").toLowerCase().includes(q)
      );
    },

    anyResults() {
      const q = this.queryString;
      if (!q) return true;

      if (this.selectedCategory === 0) {
        return this.menus.some((menu) =>
          (menu.name || "").toLowerCase().includes(q)
        );
      } else {
        return this.menus.some(
          (menu) =>
            menu.categoryId === this.selectedCategory &&
            (menu.name || "").toLowerCase().includes(q)
        );
      }
    },

    categoriesToShow() {
      const q = this.queryString;
      if (!q) return this.categories;

      return this.categories.filter((cat) => {
        const cachedMenus = this.getCachedFilteredMenus(cat.id);
        return cachedMenus.length > 0;
      });
    },

    headerBackgroundStyle() {
      return {
        backgroundImage: `url('${CONFIG.BANNER_URL}')`,
      };
    },
  },

  watch: {
    debouncedSearchQuery() {
      this.menuCache.clear();
    },
  },

  async mounted() {
    await this.initializeApp();
  },

  methods: {
    async initializeApp() {
      try {
        await Promise.all([
          this.loadCategories(),
          this.loadMenuData(),
          this.loadBestSellers(),
        ]);

        this.loadCart();

        console.log("App initialized successfully");
      } catch (error) {
        console.error("Error initializing app:", error);
        this.showError("Gagal memuat data. Silakan refresh halaman.");
      } finally {
        this.isLoading = false;
      }
    },

    async loadCategories() {
      try {
        const response = await api.get("/categories");
        const items = response.data?.data ?? response.data;

        if (!items || !Array.isArray(items))
          throw new Error("No data received");

        this.categories = items
          .map((cat) => ({
            id: cat.id,
            name: cat.name || cat.nama || "",
            urutan:
              typeof cat.urutan !== "undefined"
                ? cat.urutan
                : typeof cat.order !== "undefined"
                ? cat.order
                : 99,
          }))
          .sort((a, b) => {
            if (a.urutan !== b.urutan) {
              return a.urutan - b.urutan;
            }
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          });
      } catch (error) {
        console.error("Error loading categories:", error);
        this.showError("Gagal memuat kategori.");
      }
    },

    async loadMenuData() {
      try {
        const menuResponse = await api.get("/menus?sort_by=created_at");

        this.menus = menuResponse.data.data.map((item) => ({
          ...item,
          image: item.image_url,
          name: item.name,
          price: item.price,
          description: item.description, // ⭐ TAMBAHKAN DESCRIPTION DARI API
          categoryId: item.category_id,
          variants: item.variants || [],
          additionals: item.additionals || [], // ⭐ TAMBAHKAN INI
          isOutOfStock: !item.is_available,
          isBestSeller: item.is_best_seller,
        }));

        console.log("Menu loaded:", this.menus.length);
      } catch (error) {
        console.error("Error loading menu:", error);
        throw error;
      }
    },

    async loadBestSellers() {
      try {
        const response = await api.get(
          `/menus?best_seller=1&sort_by=created_at`
        );
        this.bestSellerIds = response.data.data.map((item) => item.id);
      } catch (error) {
        console.error("Error loading best sellers:", error);
        this.showError("Gagal memuat best seller.");
      }
    },

    async refreshMenu() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;
      this.menuCache.clear();

      try {
        await Promise.all([
          this.loadCategories(),
          this.loadMenuData(),
          this.loadBestSellers(),
        ]);

        await new Promise((resolve) =>
          setTimeout(resolve, CONFIG.ANIMATION.REFRESH_DELAY)
        );

        console.log("Menu refreshed");
      } catch (error) {
        console.error("Error refreshing menu:", error);
        this.showError("Gagal memperbarui menu. Silakan coba lagi.");
      } finally {
        this.isRefreshing = false;
      }
    },

    detectSpecialCategories() {
      this.specialCategories.dimsum = this.categories
        .filter((cat) => {
          const name = cat.name.toLowerCase();
          return CONFIG.SPECIAL_CATEGORIES.DIMSUM.some((keyword) =>
            name.includes(keyword)
          );
        })
        .map((cat) => cat.id);

      if (this.specialCategories.dimsum.length > 0) {
        console.log(
          "Dimsum categories detected:",
          this.specialCategories.dimsum
        );
      }
    },

    selectCategory(id) {
      if (id === this.selectedCategory) return;

      const order = this.categoryOrder;
      const prevIdx = order.indexOf(this.selectedCategory);
      const nextIdx = order.indexOf(id);

      this.slideDirection = nextIdx > prevIdx ? "left" : "right";
      this.tempSelectedCategory = id;

      setTimeout(() => {
        this.selectedCategory = id;
        this.isTransitioning = true;

        setTimeout(() => {
          this.isTransitioning = false;
          this.tempSelectedCategory = null;
        }, CONFIG.ANIMATION.CATEGORY_TRANSITION_END);
      }, CONFIG.ANIMATION.CATEGORY_TRANSITION_START);
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = this.searchQuery;
      }, CONFIG.ANIMATION.SEARCH_DEBOUNCE);
    },

    getCachedFilteredMenus(catId) {
      const cacheKey = `${catId}-${this.queryString}`;

      if (this.menuCache.has(cacheKey)) {
        return this.menuCache.get(cacheKey);
      }

      const q = this.queryString;
      const filtered = this.menus.filter(
        (menu) =>
          menu.categoryId === catId &&
          (menu.name || "").toLowerCase().includes(q)
      );

      this.menuCache.set(cacheKey, filtered);
      return filtered;
    },

    loadCart() {
      this.cart = JSON.parse(localStorage.getItem("cart") || "[]");
      this.cartCount = this.cart.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );
    },

    addToCart(menu) {
      let cart = JSON.parse(localStorage.getItem("cart") || "[]");

      const menuVarian = menu.varian || null;
      const quantity = menu.quantity && menu.quantity > 0 ? menu.quantity : 1;

      const idx = cart.findIndex(
        (i) =>
          i.id === menu.id &&
          i.isLocked === (menu.isLocked || false) &&
          ((!i.varian && !menu.varian) ||
            (i.varian && menu.varian && i.varian.id === menu.varian.id))
      );

      if (idx !== -1 && !cart[idx].isLocked && !menu.isLocked) {
        cart[idx].quantity += quantity;
      } else {
        const newItem = {
          id: menu.id,
          name: menu.name,
          price: menu.price,
          basePrice: menu.price || menu.basePrice,
          kategori_id: menu.kategori_id,
          varian: menuVarian,
          quantity,
          note: menu.note || "",
          isLocked: menu.isLocked || false,
          selectedAdditionalId: menu.selectedAdditionalId || null,
          additionals: menu.additionals || [],
        };

        cart.push(newItem);
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      this.loadCart();
      this.triggerCartAnimation();
    },

    // ✅ UPDATE: Handle Add To Cart - Buka Modal kalau ada variant
    handleAddToCart(item) {
      const variants = this.getMenuVariants(item);

      // Jika ada variants, buka modal
      if (variants && variants.length > 0) {
        this.openModal(item, variants);
        return;
      }

      // Jika tidak ada variant, langsung add to cart
      const isDimsum = this.isCategorySpecial(item.kategori_id, "dimsum");

      if (isDimsum) {
        const menuWithDefaults = {
          ...item,
          isLocked: item.isLocked || false,
          additionalPrice: item.additionalPrice || 0,
          dimsumOption: item.dimsumOption || "ori",
          basePrice: item.price,
        };
        this.addToCart(menuWithDefaults);
      } else {
        this.addToCart(item);
      }

      this.activeVariantId = null;
    },

    // ✅ TAMBAHAN: Open Modal
    openModal(product, variants) {
      this.selectedProduct = {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        description: product.description, // ⭐ TAMBAH DESCRIPTION
        categoryId: product.categoryId,
        isOutOfStock: product.isOutOfStock,
        additionals: product.additionals || [], // ⭐ Ini udah bener, cuma pastikan product.additionals ada
      };
      this.selectedProductVariants = variants;
      this.isModalOpen = true;
    },

    // ✅ TAMBAHAN: Close Modal
    closeModal() {
      this.isModalOpen = false;
      this.selectedProduct = {};
      this.selectedProductVariants = [];
    },

    // ✅ TAMBAHAN: Handle Add from Modal
    handleAddFromModal(cartItem) {
      this.addToCart(cartItem);
      this.closeModal();
    },

    triggerCartAnimation() {
      this.isCartAnimating = true;
      setTimeout(() => {
        this.isCartAnimating = false;
      }, CONFIG.ANIMATION.CART_ANIMATION);
    },

    goToCart() {
      this.isNavigatingToCart = true;
      setTimeout(() => {
        this.$router.push({ name: "CartPage" });
      }, 800);
    },

    getMenuVariants(menu) {
      if (!menu) return [];
      try {
        if (menu.variants) {
          const variants = menu.variants.map((variant) => {
            if (typeof variant === "string") {
              const tempId = variant.toLowerCase().replace(/\s+/g, "-");
              return { id: tempId, name: variant };
            }
            return { id: variant.id || "", name: variant.name || "" };
          });
          return Array.isArray(variants) ? variants : [];
        }
      } catch (error) {
        console.error("Error parsing variants for menu:", menu.id, error);
      }
      return [];
    },

    setActiveVariant(id) {
      this.activeVariantId = id;
    },

    isCategorySpecial(categoryId, type) {
      return this.specialCategories[type]?.includes(categoryId) || false;
    },

    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = null;
      }, 5000);
    },

    formatPrice(price) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .format(price)
        .replace("Rp", "")
        .trim();
    },

    getTotalPrice() {
      return this.cart.reduce((total, item) => {
        const basePrice = item.basePrice || item.price || 0;
        const additionalPrice = item.additionalPrice || 0;
        const itemTotal = (basePrice + additionalPrice) * (item.quantity || 1);
        return total + itemTotal;
      }, 0);
    },
  },
};
</script>

<style scoped>
.bg-latte {
  background-color: #e1c19b;
}
.bg-espresso {
  background-color: #805b2f;
}
.bg-mocha {
  background-color: #7a3500;
}

.text-cream {
  color: #fff7e8;
}
.text-latte {
  color: #e1c19b;
}
.text-caramel {
  color: #bd925f;
}
.text-espresso {
  color: #805b2f;
}
.text-mocha {
  color: #7a3500;
}
.text-roast {
  color: #341100;
}

.border-espresso {
  border-color: #805b2f;
}
.focus\:border-espresso:focus {
  border-color: #805b2f;
  outline: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
::-webkit-scrollbar {
  display: none !important;
}
* {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

/* Skeleton Loading - Fresh Pulse Design */
.skeleton-card {
  background: white;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8ecf1;
}

.skeleton-image {
  width: 100%;
  padding-top: 100%;
  background: linear-gradient(90deg, #fff7e8 25%, #f5e6d3 50%, #fff7e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-title {
  height: 0.875rem;
  background: linear-gradient(90deg, #fff7e8 25%, #f5e6d3 50%, #fff7e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
  margin-bottom: 0.25rem;
}

.skeleton-price {
  height: 0.75rem;
  width: 60%;
  background: linear-gradient(90deg, #fff7e8 25%, #f5e6d3 50%, #fff7e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
}

.skeleton-button {
  height: 2.25rem;
  background: linear-gradient(90deg, #fff7e8 25%, #f5e6d3 50%, #fff7e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.75rem;
  margin-top: 0.25rem;
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #fff7e8 25%, #f5e6d3 50%, #fff7e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Slide Transitions - Smooth & Fresh */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
}

.slide-left-enter-from {
  transform: translate3d(40px, 0, 0);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translate3d(-40px, 0, 0);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translate3d(-40px, 0, 0);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translate3d(40px, 0, 0);
  opacity: 0;
}

/* Slide Up Animation - Cart Bar */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.slide-up-enter-from {
  transform: translateY(140%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(140%);
  opacity: 0;
}

</style>
